/** OneTrust "Performance / Analytics" cookie category. */
export const ANALYTICS_CONSENT_CATEGORY = 'C0002';

/** How long to wait for the OneTrust SDK before logging that it did not load. */
const SDK_LOAD_WARNING_DELAY_MS = 10000;

interface PendingCallback {
  categoryId: string;
  callback: () => void;
}

const pending: PendingCallback[] = [];
let installedWrapper: (() => void) | undefined;
let sdkWarningScheduled = false;

const isOneTrustPresent = (): boolean => !!document.querySelector('script[data-domain-script]');

const isCategoryActive = (categoryId: string): boolean => {
  const activeGroups = window.OnetrustActiveGroups;
  return !!activeGroups && activeGroups.split(',').includes(categoryId);
};

const flushConsented = () => {
  for (let i = pending.length - 1; i >= 0; i--) {
    if (isCategoryActive(pending[i].categoryId)) {
      const [{ callback }] = pending.splice(i, 1);
      callback();
    }
  }
};

/** Hooks OneTrust's `OptanonWrapper` once, keeping any wrapper that was already there. */
const ensureWrapperInstalled = () => {
  if (window.OptanonWrapper === installedWrapper && installedWrapper) {
    return;
  }
  const previousWrapper = window.OptanonWrapper;
  installedWrapper = () => {
    previousWrapper?.();
    flushConsented();
  };
  window.OptanonWrapper = installedWrapper;
};

/**
 * Runs the callback once the visitor has consented to the given OneTrust category.
 * When OneTrust is not on the page (local dev, private cloud, or no ONETRUST_DOMAIN_SCRIPT configured)
 * the callback runs immediately. Otherwise it runs as soon as the category is active, which is checked now
 * and on every OneTrust `OptanonWrapper` call (initial load and each consent change).
 * If the OneTrust SDK never loads (e.g. blocked by an ad blocker), the callback never runs and a warning is logged.
 */
export const runWhenConsented = (categoryId: string, callback: () => void): void => {
  if (!isOneTrustPresent()) {
    callback();
    return;
  }

  pending.push({ categoryId, callback });
  ensureWrapperInstalled();
  flushConsented();

  if (pending.length > 0 && !sdkWarningScheduled) {
    sdkWarningScheduled = true;
    setTimeout(() => {
      if (!window.OneTrust) {
        console.warn('OneTrust SDK did not load; consent-gated scripts (analytics) will not start.');
      }
    }, SDK_LOAD_WARNING_DELAY_MS);
  }
};
